import React, { useEffect, useState } from 'react';
import { JobsiteAndProject } from './jobsiteType';
import JobsiteApi from './jobsiteApi';
import { useNavigate } from 'react-router-dom';
import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import Spinner from '../components/ui/spinner';
import { X, Trash, ChevronUp, ChevronDown, FilePlus, Edit } from 'lucide-react';
import { formatTimestamp, formatAddress } from '../lib/global_function';

const JobsitePage: React.FC = () => {
  const breadcrumbs = [
    { label: 'Project', path: '/main/project' },
    { label: 'Jobsite', path: '/main/project/jobsite' },
  ];

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [selectedJobsite, setSelectedJobsite] = useState<JobsiteAndProject | null>(null);
  const [jobsiteAndProject, setJobsiteAndProject] = useState<JobsiteAndProject[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchJobsite = async () => {
      setIsLoading(true);
      try {
        const fetchedJobsites = await JobsiteApi.getJobsiteAndProject();
        setJobsiteAndProject(fetchedJobsites);
      } catch (error) {
        if (error instanceof Error) {
          setErrorMessage(error.message);
        } else {
          setErrorMessage('An unknown error occurred.');
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchJobsite();
  }, []);

  const handleEditJobsite = (jobsiteAndProject: JobsiteAndProject) => {
    navigate('/main/project/jobsite/update', { state: { jobsiteAndProject } });
  };

  const handleDeleteJobsite = () => {
    if (selectedJobsite) {
      navigate('/main/project/jobsite/delete', { state: { selectedJobsite } });
    }
  };

  const handleSortBy = (key: 'name' | 'created_at', order: 'asc' | 'desc') => {
    const sortedJobsites = [...jobsiteAndProject].sort((a, b) => {
      const compareA = key === 'created_at' ? new Date(a[key]).getTime() : a[key].toLowerCase();
      const compareB = key === 'created_at' ? new Date(b[key]).getTime() : b[key].toLowerCase();
      if (order === 'asc') return compareA > compareB ? 1 : -1;
      return compareA < compareB ? 1 : -1;
    });
    setJobsiteAndProject(sortedJobsites);
  };

  const handleCreateJobsite = () => {
    navigate('/main/project/jobsite/create');
  };

  if (isLoading) {
    return (
      <>
        <HorizontalMenu />
        <Spinner />
      </>
    );
  }

  return (
    <>
      <HorizontalMenu />
      <div className="container-safe py-6">
        <Breadcrumbs items={breadcrumbs} />

        <div className="mt-6">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-semibold text-foreground">Jobsite List</h1>
            <Button size="medium" onClick={handleCreateJobsite}>
              <FilePlus size={18} />
              Create Jobsite
            </Button>
          </div>

          {errorMessage && (
            <Alert variant="error" className="mb-6">
              {errorMessage}
            </Alert>
          )}

          {jobsiteAndProject.length > 0 ? (
            <div className="space-y-4">
              {/* Selection and Action Bar */}
              <div className="mb-6 p-4 bg-gray-50 rounded-lg flex flex-wrap items-center gap-3 text-sm">
                <div className="flex items-center gap-2">
                  {selectedJobsite ? (
                    <>
                      <button
                        onClick={() => setSelectedJobsite(null)}
                        className="text-gray-600 hover:text-gray-900"
                        title="Clear Selection"
                      >
                        <X className="size-5" />
                      </button>
                      <Badge variant="default">1 jobsite selected</Badge>
                    </>
                  ) : (
                    <span className="text-gray-500">No jobsite selected</span>
                  )}
                </div>

                <span className="text-gray-300">|</span>

                <button
                  onClick={selectedJobsite ? () => handleEditJobsite(selectedJobsite) : undefined}
                  disabled={!selectedJobsite}
                  className={`flex items-center gap-2 text-sm font-medium transition-colors ${selectedJobsite
                      ? 'text-primary hover:text-primary-hover cursor-pointer'
                      : 'text-gray-400 cursor-not-allowed'
                    }`}
                  title={selectedJobsite ? 'Edit Jobsite' : 'Select a jobsite to edit'}
                >
                  <Edit size={16} />
                  Edit
                </button>
                <span className="text-gray-300">|</span>
                <button
                  onClick={selectedJobsite ? handleDeleteJobsite : undefined}
                  disabled={!selectedJobsite}
                  className={`flex items-center gap-2 text-sm font-medium transition-colors ${selectedJobsite
                      ? 'text-primary hover:text-primary-hover cursor-pointer'
                      : 'text-gray-400 cursor-not-allowed'
                    }`}
                  title={selectedJobsite ? 'Delete Jobsite' : 'Select a jobsite to delete'}
                >
                  <Trash size={16} />
                  Delete
                </button>
              </div>

              {/* Jobsites Table */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="w-12 px-4 py-3"></th>
                        <th className="px-4 py-3 text-left">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-700">Jobsite Name</span>
                            <div className="flex flex-col">
                              <button
                                onClick={() => handleSortBy('name', 'asc')}
                                className="hover:text-primary transition-colors"
                                title="Sort Ascending"
                              >
                                <ChevronUp size={14} />
                              </button>
                              <button
                                onClick={() => handleSortBy('name', 'desc')}
                                className="hover:text-primary transition-colors -mt-1"
                                title="Sort Descending"
                              >
                                <ChevronDown size={14} />
                              </button>
                            </div>
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Description</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Address</th>
                        <th className="px-4 py-3 text-left">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold text-gray-700">Created On</span>
                            <div className="flex flex-col">
                              <button
                                onClick={() => handleSortBy('created_at', 'asc')}
                                className="hover:text-primary transition-colors"
                                title="Sort Ascending"
                              >
                                <ChevronUp size={14} />
                              </button>
                              <button
                                onClick={() => handleSortBy('created_at', 'desc')}
                                className="hover:text-primary transition-colors -mt-1"
                                title="Sort Descending"
                              >
                                <ChevronDown size={14} />
                              </button>
                            </div>
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {jobsiteAndProject.map((jobsite, index) => (
                        <React.Fragment key={jobsite.jobsite_id}>
                          <tr
                            className={`border-b border-gray-200 hover:bg-gray-50 transition-colors ${selectedJobsite?.jobsite_id === jobsite.jobsite_id ? 'bg-blue-50' : ''
                              }`}
                          >
                            <td className="px-4 py-3 text-center">
                              <input
                                type="radio"
                                name="jobsiteSelect"
                                onChange={() => setSelectedJobsite(jobsite)}
                                checked={selectedJobsite?.jobsite_id === jobsite.jobsite_id}
                                className="w-4 h-4 text-primary focus:ring-primary cursor-pointer"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <button
                                onClick={() => handleEditJobsite(jobsite)}
                                className="text-primary hover:text-primary-hover font-medium hover:underline transition-colors text-left"
                              >
                                {jobsite.name}
                              </button>
                            </td>
                            <td className="px-4 py-3 text-gray-700">
                              {jobsite.jobsite_description || (
                                <span className="text-gray-400 italic">N/A</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-gray-700">
                              {formatAddress(
                                jobsite.address_1,
                                jobsite.address_2 || '',
                                jobsite.city,
                                jobsite.state,
                                jobsite.zip
                              ) || <span className="text-gray-400 italic">N/A</span>}
                            </td>
                            <td className="px-4 py-3 text-gray-700">{formatTimestamp(jobsite.created_at)}</td>
                          </tr>
                          <tr className="border-b border-gray-200 bg-gray-50">
                            <td></td>
                            <td colSpan={4} className="px-4 py-2">
                              <div className="flex items-center gap-2 text-xs text-gray-600">
                                <span>Project:</span>
                                <span className="font-medium text-gray-700">{jobsite.project_name}</span>
                                <span className="text-gray-400">•</span>
                                <span>Type:</span>
                                <Badge variant="secondary" className="text-xs">
                                  {jobsite.project_type}
                                </Badge>
                              </div>
                            </td>
                          </tr>
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-8 bg-white border border-gray-200 rounded-lg text-center">
              <p className="text-gray-600 mb-4">No jobsites found. Create your first jobsite to get started.</p>
              <Button size="medium" onClick={handleCreateJobsite}>
                <FilePlus size={18} />
                Create Jobsite
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default JobsitePage;